import { GltfMemoryBudgetExceededError } from '@/plugins/mesh/errors'
import type { GltfResourcePreflightReport } from '@/plugins/mesh/loaders/gltfLowMemory/preflightTypes'
import type { GltfLoadingOptions, GltfTextureSizingReport } from '@/plugins/mesh/types'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('mesh:gltf:preflight')

/** 在正式加载前固定本次纹理计划，并将动态预算传递给加载期复核。 */
export function applyGltfResourcePlan(
  loading: GltfLoadingOptions,
  resources: GltfResourcePreflightReport,
  source: string
): { loading: GltfLoadingOptions; report: GltfTextureSizingReport } {
  const selected = resources.selected
  const cpuBudget = resources.budget.cpuBytes ?? Infinity
  const geometryBudget = Math.min(
    loading.budget.maxFinalGeometryBytes,
    resources.budget.gpuBytes ?? Infinity
  )
  if (!selected) {
    const smallest = resources.candidates[resources.candidates.length - 1]
    const cpuBytes =
      smallest.estimatedCpuPeakBytes ??
      smallest.knownCpuPeakBytes ??
      smallest.estimatedGeometryBytes
    const cpuExceeded = cpuBytes > cpuBudget
    const geometryExceeded = smallest.estimatedGeometryBytes > geometryBudget
    throw new GltfMemoryBudgetExceededError(
      source,
      cpuExceeded
        ? 'budget-transient-exceeded'
        : geometryExceeded
          ? 'budget-final-exceeded'
          : 'budget-texture-exceeded',
      cpuExceeded
        ? cpuBytes
        : geometryExceeded
          ? smallest.estimatedGeometryBytes
          : (smallest.estimatedTextureBytes ?? smallest.knownTextureBytes ?? 0),
      cpuExceeded
        ? cpuBudget
        : geometryExceeded
          ? geometryBudget
          : (smallest.availableTextureBytes ?? Infinity)
    )
  }
  const effectiveBudgetBytes = selected.availableTextureBytes ?? Infinity
  log.debug(
    `event=mesh.gltf.preflight source=${source} confidence=${resources.confidence} images=${resources.imageCount} textures=${resources.textureCount} readBytes=${resources.readBytes} cpuPeakBytes=${selected.estimatedCpuPeakBytes} gpuBytes=${selected.estimatedGpuBytes} maxSize=${selected.maxSize ?? 'original'} textureConcurrency=${selected.textureConcurrency} loadConcurrency=${selected.loadConcurrency}`
  )
  return {
    loading: {
      ...loading,
      texture: {
        ...loading.texture,
        maxSize: selected.maxSize ?? Infinity,
        maxConcurrentDecodes: selected.textureConcurrency,
      },
      budget: {
        ...loading.budget,
        maxTextureBytes: effectiveBudgetBytes,
        maxFinalGeometryBytes: Math.min(geometryBudget, cpuBudget),
        maxTransientBytes: Math.min(loading.budget.maxTransientBytes, cpuBudget),
        maxRangeCacheBytes: Math.min(loading.budget.maxRangeCacheBytes, cpuBudget),
        maxMergedBatchBytes: Math.min(
          loading.budget.maxMergedBatchBytes,
          geometryBudget,
          cpuBudget
        ),
      },
    },
    report: {
      textureCount: resources.textureCount,
      selectedMaxSize: selected.maxSize ?? Infinity,
      estimatedTextureBytes: selected.estimatedTextureBytes,
      effectiveBudgetBytes,
      resources,
    },
  }
}
